/**
 * PatientRowExpanded - Drawer Detail View
 *
 * Shows detailed patient information in a drawer with:
 * - Notes section (at top)
 * - Call, Waiting Room, and Timeline sections
 */

import React from 'react';
import { Card, Typography, Tag, Timeline } from 'antd';
import {
  PhoneOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  UserOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Text, Paragraph } = Typography;

const PatientRowExpanded = ({ record }) => {
  if (!record) return null;

  // Format time for display
  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).utc().format('HH:mm');
  };

  // Build timeline items based on available data
  const buildTimelineItems = () => {
    const items = [];

    // Call started
    if (record.call?.startTime) {
      items.push({
        time: record.call.startTime,
        label: 'Call Started',
        description: `${record.call.callType === 'video' ? 'Video' : 'Voice'} call - ${record.call.callStep || 'Unknown queue'}`,
        color: 'blue',
        icon:
          record.call.callType === 'video' ? (
            <VideoCameraOutlined />
          ) : (
            <PhoneOutlined />
          ),
      });
    }

    // Appointment created
    if (record.appointment?.createdAt) {
      items.push({
        time: record.appointment.createdAt,
        label: 'Appointment Created',
        description: `${record.appointment.urgency || 'Standard'} - ${record.appointment.triageCategory ? `CAT${record.appointment.triageCategory}` : 'No triage'}`,
        color: 'green',
        icon: <MedicineBoxOutlined />,
      });
    }

    // Agent assigned
    if (record.call?.agentName && record.call?.status !== 'created') {
      items.push({
        time: null, // We don't have exact time for this
        label: 'Assigned to Agent',
        description: record.call.agentName,
        color: 'blue',
        icon: <UserOutlined />,
      });
    }

    // Current status
    if (record.overallStatus) {
      const isCompleted = record.overallStatus === 'Completed';
      items.push({
        time: null,
        label: record.overallStatus,
        description:
          record.waitingRoom?.stopReason ||
          (isCompleted ? 'Appointment completed' : 'Currently active'),
        color: isCompleted ? 'gray' : 'green',
        icon: isCompleted ? <CheckCircleOutlined /> : <LoadingOutlined />,
        current: !isCompleted,
      });
    }

    // Sort by time (items with time first)
    items.sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return 1;
      if (!b.time) return -1;
      return dayjs(a.time).valueOf() - dayjs(b.time).valueOf();
    });

    return items;
  };

  const timelineItems = buildTimelineItems();

  // Section header style (consistent across all sections)
  const sectionHeaderStyle = {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };

  return (
    <div
      style={{
        padding: '12px',
        backgroundColor: '#fafafa',
        minHeight: '100%',
        fontSize: 12,
      }}
    >
      {/* Section: Notes */}
      {record.appointment?.note && (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={sectionHeaderStyle}>
            <FileTextOutlined style={{ marginRight: 4 }} />
            Notes
          </Text>
          <Card
            size="small"
            style={{ marginTop: 6 }}
            styles={{ body: { padding: '8px 12px' } }}
          >
            <Paragraph
              style={{
                whiteSpace: 'pre-wrap',
                marginBottom: 0,
                fontSize: 12,
              }}
            >
              {record.appointment.note}
            </Paragraph>
          </Card>
        </div>
      )}

      {/* Section: Call Details */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={sectionHeaderStyle}>
          <PhoneOutlined style={{ marginRight: 4 }} />
          Call Details
        </Text>
        <Card
          size="small"
          style={{ marginTop: 6 }}
          styles={{ body: { padding: '8px 12px' } }}
        >
          {record.call ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px 12px',
              }}
            >
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Type
                </Text>
                <div>
                  {record.call.callType === 'video' ? (
                    <Tag
                      icon={<VideoCameraOutlined />}
                      color="#6f86d6"
                      style={{ margin: 0, fontSize: 11 }}
                    >
                      Video
                    </Tag>
                  ) : (
                    <Tag
                      icon={<PhoneOutlined />}
                      color="#4eb5b0"
                      style={{ margin: 0, fontSize: 11 }}
                    >
                      Voice
                    </Tag>
                  )}
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Status
                </Text>
                <div>
                  <Tag
                    color={
                      record.call.status === 'assigned'
                        ? 'blue'
                        : record.call.status === 'wrapping'
                          ? 'purple'
                          : 'default'
                    }
                    style={{ margin: 0, fontSize: 11 }}
                  >
                    {record.call.status || '-'}
                  </Tag>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Queue
                </Text>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    {record.call.callStep || '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Agent
                </Text>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    {record.call.agentName || '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Case ID
                </Text>
                <div>
                  <Text
                    style={{ fontSize: 12 }}
                    copyable={record.call.caseId ? true : false}
                  >
                    {record.call.caseId || '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Triage
                </Text>
                <div>
                  {record.appointment?.triageCategoryNum ? (
                    <Tag
                      color={
                        record.appointment.triageCategoryNum <= 2
                          ? 'red'
                          : record.appointment.triageCategoryNum === 3
                            ? 'orange'
                            : 'blue'
                      }
                      style={{ margin: 0, fontSize: 11 }}
                    >
                      CAT {record.appointment.triageCategoryNum}
                    </Tag>
                  ) : (
                    <Text style={{ fontSize: 12 }}>-</Text>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No active call
            </Text>
          )}
        </Card>
      </div>

      {/* Section: Waiting Room */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={sectionHeaderStyle}>
          <MedicineBoxOutlined style={{ marginRight: 4 }} />
          Waiting Room
        </Text>
        <Card
          size="small"
          style={{ marginTop: 6 }}
          styles={{ body: { padding: '8px 12px' } }}
        >
          {record.waitingRoom ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px 12px',
              }}
            >
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Wait Time
                </Text>
                <div>
                  <Text
                    strong
                    style={{ fontSize: 12 }}
                    type={
                      record.waitingRoom.waitTime > 60 ? 'danger' : undefined
                    }
                  >
                    {Number.isFinite(record.waitingRoom.waitTime)
                      ? record.waitingRoom.waitTime >= 60
                        ? `${Math.floor(record.waitingRoom.waitTime / 60)}h ${record.waitingRoom.waitTime % 60}m`
                        : `${record.waitingRoom.waitTime} min`
                      : '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  SLA
                </Text>
                <div>
                  {record.slaStatus?.percentage ? (
                    <Tag
                      color={
                        record.slaStatus.percentage >= 100
                          ? 'red'
                          : record.slaStatus.percentage >= 80
                            ? 'orange'
                            : record.slaStatus.percentage >= 50
                              ? 'gold'
                              : 'green'
                      }
                      style={{ margin: 0, fontSize: 11 }}
                    >
                      {record.slaStatus.percentage}%
                    </Tag>
                  ) : (
                    <Text style={{ fontSize: 12 }}>-</Text>
                  )}
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Clock Status
                </Text>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    {record.waitingRoom.clockStatus || '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Appointment Status
                </Text>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    {record.waitingRoom.apptStatus || '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Elapsed Time
                </Text>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    {Number.isFinite(record.waitingRoom.elapsedTime)
                      ? record.waitingRoom.elapsedTime >= 60
                        ? `${Math.floor(record.waitingRoom.elapsedTime / 60)}h ${record.waitingRoom.elapsedTime % 60}m`
                        : `${record.waitingRoom.elapsedTime} min`
                      : '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Doctor
                </Text>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    {record.waitingRoom.doctorName || '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Stop Reason
                </Text>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    {record.waitingRoom.stopReason || '-'}
                  </Text>
                </div>
              </div>
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No waiting room data
            </Text>
          )}
        </Card>
      </div>

      {/* Section: Timeline */}
      <div>
        <Text strong style={sectionHeaderStyle}>
          Journey Timeline
        </Text>
        <Card
          size="small"
          style={{ marginTop: 6 }}
          styles={{ body: { padding: '10px 12px' } }}
        >
          {timelineItems.length > 0 ? (
            <Timeline
              items={timelineItems.map((item) => ({
                color: item.color,
                dot: item.icon,
                children: (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                      }}
                    >
                      <Text strong style={{ fontSize: 11 }}>
                        {item.label}
                      </Text>
                      {item.current && (
                        <Tag color="green" style={{ fontSize: 10, margin: 0 }}>
                          Current
                        </Tag>
                      )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {item.time ? formatTime(item.time) : ''}{' '}
                      {item.description}
                    </Text>
                  </div>
                ),
              }))}
            />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No timeline data available
            </Text>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PatientRowExpanded;
